import { BLOCK_SECONDS } from '../core/pricing'
import type { Coin } from '../core/types'
import { integerRate, normalizedState, sumCoins } from './billing'
import { BLOCKS_PER_DAY, depositFigures, escrowDseq, LOW_RUNWAY_BLOCKS } from './deposits'
import { groupInstances } from './instances'
import type { Account, DomainLease, DomainLeaseList, EscrowAccountDto, EscrowDto } from './types'

// One grouping rule for Overview and Instances, so both count instances alike.
export { groupInstances }

export interface OverviewInstanceSummary {
  id: string
  dseq: string
  hostName: string
  region: string
  state: DomainLease['state']
  running: boolean
  rate: Coin
}

export interface OverviewAttentionItem {
  id: string
  severity: 'critical' | 'caution' | 'info'
  title: string
  body: string
  action?: { label: string; path: string }
}

export interface OverviewActivityItem {
  id: string
  label: string
  detail?: string
  at?: string | null
}

export type InstanceSummary = OverviewInstanceSummary
export type AttentionItem = OverviewAttentionItem
export type ActivityItem = OverviewActivityItem

export type OverviewRateState = 'charging' | 'idle' | 'unavailable'
export type OverviewRunwayState = 'known' | 'not-spending' | 'unavailable'
/** The low-runway threshold, shared by the Runway tile and the attention list. */
export type OverviewRunwayAlert = 'critical' | 'caution' | null

export interface OverviewViewModel {
  stats: {
    runningInstances: number
    /**
     * Open escrow accounts whose deployment has no lease that is running or
     * unclassified: a request waiting for offers, or one whose lease the
     * provider closed (the chain pauses that group, so no offers follow).
     */
    heldRequests: number
    /** Per block: the sum of the running leases' prices. Null when any of them is unreadable. */
    rate: Coin | null
    rateState: OverviewRateState
    prepaidBalance: Coin | null
    /** The part of the prepaid balance held by open requests. Null when unreadable. */
    heldBalance: Coin | null
    /** The shortest runway over the accounts that back a running lease (`data/deposits.ts`). */
    runwayDays: number | null
    runwayState: OverviewRunwayState
    runwayAlert: OverviewRunwayAlert
    /** An estimate, from the runway in blocks at the chain's block time. Only when known. */
    runwayEndsAt: Date | null
    /** Kept for a future spend source; always null today and never displayed. */
    spendToday: Coin | null
  }
  instances: InstanceSummary[]
  attention: AttentionItem[]
  activity: ActivityItem[]
}

export interface OverviewModelInput {
  account: Account
  leases: DomainLeaseList | null
  escrow: EscrowDto | null
  spendToday?: Coin | null
  activity?: OverviewActivityItem[] | undefined
  /** The chain height, for the deposit estimate. Without it the last settled balance is used. */
  height?: number | null | undefined
  /** Injected by tests; the page uses the current time. */
  now?: Date | undefined
}

/** No escrow accounts means nothing is prepaid — reported as zero, not as unknown. */
const ZERO_PREPAID: Coin = { denom: 'uddt', amount: '0' }

export { BLOCKS_PER_DAY, LOW_RUNWAY_BLOCKS }
const IDLE_RATE: Coin = { denom: 'uddt', amount: '0' }

function usableCoin(coin: Coin | null | undefined): Coin | null {
  return coin?.denom && coin.amount ? coin : null
}

function isOpen(account: EscrowAccountDto): boolean {
  return normalizedState(account.state) === 'active'
}

/**
 * Split the open escrow accounts into those that back a running lease and those
 * that only hold money for an open request with nothing running.
 *
 * `MsgCreateDeployment` opens the deployment's escrow account with its deposit
 * before any lease exists, and a provider-side lease close pauses the group but
 * leaves the deployment and its escrow open. Either way the money is set aside,
 * not spent. An account whose lease is out of funds or unclassified is neither:
 * it is left out of both, rather than described as something it may not be.
 */
function splitEscrow(leases: DomainLeaseList | null, escrow: EscrowDto | null) {
  // Without the leases nothing tells a held account from a leased one.
  if (!leases) return { leased: [], held: [] }
  const activeDseqs = new Set(leases.leases.filter((lease) => lease.state === 'active').map((lease) => lease.dseq))
  const liveDseqs = new Set(leases.leases.filter((lease) => lease.state !== 'closed').map((lease) => lease.dseq))
  const accounts = Array.isArray(escrow?.accounts) ? escrow.accounts.filter((account) => Boolean(account) && typeof account === 'object') : []
  return {
    leased: accounts.filter((account) => isOpen(account) && activeDseqs.has(escrowDseq(account))),
    held: accounts.filter((account) => isOpen(account) && !liveDseqs.has(escrowDseq(account))),
  }
}

/**
 * The shortest runway in blocks over the leased accounts, from the same
 * figures Billing and Instances use: unknown unless every one of them has a
 * readable rate and deposit, because the shortest is the one that matters.
 */
function runwayBlocks(leased: EscrowAccountDto[], leases: DomainLeaseList | null, height: number | null): number | null {
  if (!leases || leased.length === 0) return null
  const blocks = leased.map((account) => depositFigures(account, leases.leases, height).runwayBlocks)
  if (blocks.some((value) => value === null)) return null
  return Math.min(...(blocks as number[]))
}

function runningRate(leases: DomainLeaseList | null): { rate: Coin | null; rateState: OverviewRateState } {
  const running = (leases?.leases ?? []).filter((lease) => lease.state === 'active')
  if (running.length === 0) return { rate: IDLE_RATE, rateState: 'idle' }
  // A missing price on any running lease makes the whole rate unavailable:
  // a partial sum would be a confident number that is too low.
  const rate = sumCoins(running.map((lease) => integerRate(lease.rate)))
  return rate ? { rate, rateState: 'charging' } : { rate: null, rateState: 'unavailable' }
}

function runwayAlert(blocks: number | null): OverviewRunwayAlert {
  if (blocks == null || blocks > LOW_RUNWAY_BLOCKS) return null
  return blocks === 0 ? 'critical' : 'caution'
}

function attentionFor(
  account: Account,
  instances: OverviewInstanceSummary[],
  escrow: EscrowDto | null,
  runway: number | null,
): OverviewAttentionItem[] {
  const items: OverviewAttentionItem[] = []

  // The chain creates an account record the first time an address receives
  // coins. Until then the address cannot sign anything at all — gas has to come
  // from somewhere — so telling a tenant to sign, or to rent a machine, asks
  // for something that cannot succeed. Funding is the only move available.
  if (!account.exists) {
    items.push({
      id: 'account-needs-funds',
      severity: 'info',
      title: 'Add funds to activate your account',
      body: 'The chain creates your account the first time it receives money. Until then it cannot sign a rental.',
      action: { label: 'Add funds', path: '/app/billing' },
    })
  }

  for (const instance of instances) {
    if (instance.state === 'insufficient-funds') {
      items.push({
        id: `funds-${instance.id}`,
        severity: 'critical',
        title: 'This instance ran out of funds and was closed',
        body: `${instance.hostName} stopped it when its deposit was used up. Rent again to restart the workload.`,
        action: { label: 'Rent again', path: '/app/rent' },
      })
    } else if (instance.state === 'unknown') {
      items.push({
        id: `unknown-${instance.id}`,
        severity: 'caution',
        title: 'Instance status needs checking',
        body: `We could not classify the current state for ${instance.hostName}.`,
        action: { label: 'Open instance', path: `/app/instances/${encodeURIComponent(instance.dseq)}` },
      })
    }
  }

  if (instances.some((instance) => instance.running) && escrow && escrow.accounts.length === 0) {
    items.push({
      id: 'missing-escrow',
      severity: 'caution',
      title: 'Prepaid balance is not available',
      body: 'The chain has not reported an escrow balance for an active instance.',
      action: { label: 'Open billing', path: '/app/billing' },
    })
  } else if (runwayAlert(runway)) {
    items.push({
      id: 'low-runway',
      severity: runwayAlert(runway)!,
      title: runway === 0 ? 'Your prepaid balance is empty' : 'Your prepaid balance is running low',
      body: 'Top up your instances before the current runway expires.',
      action: { label: 'Top up an instance', path: '/app/instances' },
    })
  }

  return items
}

/**
 * Build the tenant-facing Overview model from already-normalized query data.
 * This deliberately leaves spend and activity nullable until trustworthy chain
 * sources exist; the UI must not infer either value from a current lease rate.
 * The rate itself is a chain fact (the lease price), and is reported as such.
 */
export function buildOverviewModel(input: OverviewModelInput): OverviewViewModel {
  const instances = groupInstances(input.leases)
  const active = instances.filter((instance) => instance.running).length
  const { leased, held } = splitEscrow(input.leases, input.escrow)
  const blocks = active > 0 ? runwayBlocks(leased, input.leases, input.height ?? null) : null
  const { rate, rateState } = runningRate(input.leases)
  const now = input.now ?? new Date()

  return {
    stats: {
      runningInstances: active,
      heldRequests: held.length,
      rate,
      rateState,
      spendToday: input.spendToday ?? null,
      // A tenant with no escrow has nothing prepaid, which is a fact worth
      // stating. Only an escrow the console could not read at all is unknown.
      prepaidBalance: input.escrow ? (input.escrow.accounts.length > 0 ? usableCoin(input.escrow.totals?.balance) : ZERO_PREPAID) : null,
      heldBalance: input.escrow && input.leases ? (held.length > 0 ? sumCoins(held.map((account) => account.balance)) : ZERO_PREPAID) : null,
      runwayDays: blocks == null ? null : Math.floor(blocks / BLOCKS_PER_DAY),
      // Nothing running means nothing is drawing on the balance, whatever an
      // open request holds; that is a state, not a missing number.
      runwayState: active === 0 ? 'not-spending' : blocks == null ? 'unavailable' : 'known',
      runwayAlert: runwayAlert(blocks),
      runwayEndsAt: blocks == null ? null : new Date(now.getTime() + blocks * BLOCK_SECONDS * 1000),
    },
    instances,
    attention: attentionFor(input.account, instances, input.escrow, blocks),
    activity: input.activity ?? [],
  }
}
